import React, { useState, useEffect } from 'react';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, TrendingUp, Activity, Calendar } from 'lucide-react';
import api from '../../services/api';

const PatientAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/analytics/patients');
      setAnalytics(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load patient analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
        <button onClick={fetchAnalytics} className="ml-4 text-sm underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  // Status distribution for pie chart
  const statusData = [
    { name: 'Active', value: analytics?.status_distribution?.active || 0, color: '#10b981' },
    { name: 'Completed', value: analytics?.status_distribution?.completed || 0, color: '#3b82f6' },
    { name: 'Follow-up Pending', value: analytics?.status_distribution?.pending || 0, color: '#f59e0b' }
  ];

  // Age distribution for bar chart
  const ageData = analytics?.age_distribution ? 
    Object.entries(analytics.age_distribution).map(([age, count]) => ({
      age,
      count
    })) : [];

  // Monthly new patients
  const monthlyData = analytics?.monthly_new_patients || [];

  // Common conditions
  const conditionsData = analytics?.common_conditions || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Patients</p>
              <p className="text-3xl font-bold mt-2">{analytics?.total_patients || 0}</p>
            </div>
            <Users className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Active Patients</p>
              <p className="text-3xl font-bold mt-2">{analytics?.status_distribution?.active || 0}</p>
            </div>
            <Activity className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Completed</p>
              <p className="text-3xl font-bold mt-2">{analytics?.status_distribution?.completed || 0}</p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Follow-up Pending</p>
              <p className="text-3xl font-bold mt-2">{analytics?.status_distribution?.pending || 0}</p>
            </div>
            <Calendar className="w-12 h-12 opacity-80" />
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Patients Per Month */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">New Patients per Month</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} name="New Patients" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Pie Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Patient Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {statusData.map((status, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: status.color }}></div>
                  <span className="text-sm text-gray-700">{status.name}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{status.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Age Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ageData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8b5cf6" name="Patients" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Common Conditions */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Common Conditions</h3>
          {conditionsData.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No conditions data available
            </div>
          ) : (
            <div className="space-y-3">
              {conditionsData.slice(0, 8).map((condition, idx) => {
                const maxCount = Math.max(...conditionsData.map(c => c.count));
                const percentage = (condition.count / maxCount) * 100;
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{condition.condition}</span>
                      <span className="text-sm font-semibold text-gray-900">{condition.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Insights Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <h3 className="text-lg font-semibold mb-3">Patient Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="opacity-90 mb-1">Completion Rate</p>
            <p className="text-xl font-bold">
              {analytics?.total_patients > 0
                ? ((analytics.status_distribution.completed / analytics.total_patients) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Active Treatment Rate</p>
            <p className="text-xl font-bold">
              {analytics?.total_patients > 0
                ? ((analytics.status_distribution.active / analytics.total_patients) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Follow-up Rate</p>
            <p className="text-xl font-bold">
              {analytics?.total_patients > 0
                ? ((analytics.status_distribution.pending / analytics.total_patients) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientAnalytics;